import { useEffect, useRef, type RefObject } from "react";

/**
 * Calls `onLoad` when `target` comes within `margin` of the viewport, so a
 * list grows as the guest scrolls instead of waiting for a "Show more" click.
 *
 * The observer is rebuilt whenever `step` changes (pass the number of items
 * on screen). A fresh observer reports the sentinel's current state at once,
 * so a short batch that leaves the sentinel still in view loads the next one
 * too, instead of stalling until the guest scrolls again.
 */
export function useLoadOnScroll(
  target: RefObject<Element | null>,
  { enabled, step, onLoad, margin = "800px" }: { enabled: boolean; step: number; onLoad: () => void; margin?: string },
) {
  const load = useRef(onLoad);
  load.current = onLoad;
  useEffect(() => {
    const el = target.current;
    if (!enabled || !el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) load.current();
      },
      { rootMargin: `0px 0px ${margin} 0px` },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [target, enabled, step, margin]);
}
